import { useEffect, useLayoutEffect, useRef } from 'react';
import type { TemplateDoc } from '@bannermatic/core';
import { createMotionPlayer, Scene, type MotionPlayer } from '@bannermatic/scene';
import { resolvePreviewSrc } from './DocPreview';

/** The whole video playing in a loop, scaled to fit a `box`×`boxHeight` area. */
export function PlayingDoc({
  doc,
  box,
  boxHeight = box,
}: {
  doc: TemplateDoc;
  box: number;
  boxHeight?: number;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<MotionPlayer | null>(null);
  const scale = Math.min(box / doc.width, boxHeight / doc.height);
  useLayoutEffect(() => {
    let cancelled = false;
    const raf = requestAnimationFrame(() => {
      const scene = hostRef.current?.querySelector<HTMLElement>('.bm-scene');
      if (cancelled || !scene) return;
      const player = createMotionPlayer(scene, doc);
      playerRef.current = player;
      player.seek(0);
      player.play({ loop: true });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, [doc]);
  useEffect(() => () => playerRef.current?.destroy(), []);
  return (
    <div
      className="relative overflow-hidden"
      style={{ width: doc.width * scale, height: doc.height * scale }}
    >
      <div
        ref={hostRef}
        className="pointer-events-none absolute top-0 left-0"
        style={{
          width: doc.width,
          height: doc.height,
          transform: `scale(${scale})`,
          transformOrigin: '0 0',
        }}
      >
        <Scene doc={doc} mode="render" resolveSrc={resolvePreviewSrc} />
      </div>
    </div>
  );
}
